import React, { useCallback, useEffect, useState } from 'react';
import { dataAnalystExamplesApi } from '../../../lib/apiClient';
import type { DataAnalystExample } from '../../../types/dataAnalyst';
import { toast } from '../../../lib/toast';
import styles from './DataLakeLiveView.module.css';

/** Remembered Data Analyst queries - read and delete only; they are written automatically, never edited. */
const DataAnalystExamples: React.FC = () => {
  const [examples, setExamples] = useState<DataAnalystExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchExamples = useCallback(async () => {
    try {
      setExamples(await dataAnalystExamplesApi.list());
    } catch {
      toast.error('Failed to load analyst query examples');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExamples();
  }, [fetchExamples]);

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await dataAnalystExamplesApi.remove(id);
      setExamples((prev) => prev.filter((e) => e.id !== id));
      toast.success('Example deleted');
    } catch {
      toast.error('Failed to delete example');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section className={styles.examplesSection}>
      <div className={styles.examplesTitle}>Data Analyst - remembered queries</div>
      <div className={styles.muted}>
        The last accepted query per widget and result name. Offered to the analyst as a starting point when the same
        tables (same columns) are loaded again.
      </div>

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : examples.length === 0 ? (
        <div className={styles.empty}>No remembered queries yet.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Result name</th>
              <th>Task</th>
              <th>Used</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {examples.map((e) => (
              <tr key={e.id}>
                <td><span className={styles.sessionIdCell}>{e.resultName}</span></td>
                <td>
                  <div className={styles.paramsSummary}>{e.task}</div>
                  <details className={styles.sqlDetails}>
                    <summary>SQL</summary>
                    <pre className={styles.sqlBlock}>{e.sqlTemplate}</pre>
                  </details>
                </td>
                <td>{e.useCount}</td>
                <td><span className={styles.muted}>{new Date(e.updatedAt).toLocaleString()}</span></td>
                <td>
                  <button
                    className={styles.dropBtn}
                    disabled={deleting === e.id}
                    onClick={() => handleDelete(e.id)}
                  >
                    {deleting === e.id ? 'Deleting…' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default DataAnalystExamples;
